import { Injectable, signal } from '@angular/core';

interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const IOS_HINT_DISMISSED_KEY = 'pwa_ios_hint_dismissed';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  readonly canInstall = signal(false);
  readonly showIosHint = signal(false);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.canInstall.set(true);
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.canInstall.set(false);
      this.showIosHint.set(false);
    });

    this.showIosHint.set(this.isIosLike() && !this.isStandalone() && !this.hintDismissed());
  }

  async promptInstall(): Promise<void> {
    const prompt = this.deferredPrompt;
    if (!prompt) {
      return;
    }
    // The event is single-use; re-prompting with a consumed one throws.
    this.deferredPrompt = null;
    this.canInstall.set(false);
    await prompt.prompt();
    await prompt.userChoice;
  }

  dismissIosHint(): void {
    this.showIosHint.set(false);
    try {
      localStorage.setItem(IOS_HINT_DISMISSED_KEY, '1');
    } catch {
      // Private mode / blocked storage: the hint reappears next load, which
      // is better than breaking the dismissal.
    }
  }

  private hintDismissed(): boolean {
    try {
      return localStorage.getItem(IOS_HINT_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }

  private isStandalone(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  }

  // iPadOS 13+ Safari reports a desktop macOS user agent, and the iPad is this
  // product's primary device — plain /iPad/ sniffing would match nobody.
  private isIosLike(): boolean {
    const ua = navigator.userAgent;
    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }
}
